import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  isBuyerRole,
} from "../features/auth/onboarding";
import { useCountries } from "../features/geo/hooks";
import { usersService } from "../features/users/service";
import { parseApiError } from "../lib/http/apiErrors";
import { onboardingFlowPublicService, type StepConfig } from "../features/onboardingFlows/service";
import {
  FlowFieldRenderer,
  defaultFieldValue,
  isDynamicStepValid,
  phoneCountryCodeKey,
  splitPhoneE164,
  type FlowFieldValue,
} from "../features/onboardingFlows/FieldRenderer";

// ── Stepper progress bar ────────────────────────────────────────────────────

function StepperBar({
  current,
  steps,
  completedKeys,
  skippedKeys = [],
  onStepClick,
}: {
  current: number;
  steps: StepConfig[];
  completedKeys: string[];
  skippedKeys?: string[];
  onStepClick?: (stepIndex: number) => void;
}) {
  return (
    <div className="mb-8 overflow-x-auto pb-4 sm:overflow-visible sm:pb-0">
      <div className="flex min-w-max items-center justify-between sm:min-w-0">
        {steps.map((s, i) => {
          const done = completedKeys.includes(s.key);
          const skipped = !done && skippedKeys.includes(s.key);
          const active = i + 1 === current;
          const clickable = onStepClick && (done || skipped || i + 1 < current);
          return (
            <div key={s.key} className="flex flex-1 items-center">
              <button
                type="button"
                onClick={() => { if (clickable) onStepClick(i + 1); }}
                disabled={!clickable}
                className={`flex flex-col items-center group outline-none ${clickable ? "cursor-pointer" : "cursor-default"}`}
              >
                <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold transition-all ${
                  active  ? "border-brand bg-brand/10 text-brand" :
                  done    ? "border-brand bg-brand text-white" :
                  skipped ? "border-amber-500 bg-amber-100 text-amber-700" :
                            "border-border bg-surface text-ink-muted"
                } ${clickable && !active ? "group-hover:ring-2 group-hover:ring-brand/30" : ""}`}>
                  {done && !active ? "✓" : i + 1}
                </div>
                <span className={`mt-1 hidden text-[10px] sm:block transition-colors ${
                  active ? "font-semibold text-brand" :
                  skipped ? "text-amber-700" :
                  clickable ? "text-ink group-hover:text-brand" : "text-ink-muted"
                }`}>
                  {s.label}
                </span>
              </button>
              {i < steps.length - 1 && (
                <div className={`mx-2 h-0.5 flex-1 transition-all ${done ? "bg-brand" : skipped ? "bg-amber-400" : "bg-border"}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────

const BuyerOnboardingPage: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, refreshUser, logout } = useAuth();

  const { countries } = useCountries();

  // ── Flow config (admin-configured step list) ─────────────────────────────
  const [steps, setSteps]           = useState<StepConfig[]>([]);
  const [flowLoading, setFlowLoading] = useState(true);
  const [flowError, setFlowError]   = useState<string | null>(null);

  // ── Step state ────────────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(1);
  const [completedKeys, setCompletedKeys] = useState<string[]>([]);
  const [skippedKeys, setSkippedKeys] = useState<string[]>([]);

  // Dynamic-step field values: { [stepKey]: { [fieldKey]: value } }
  const [dynamicValues, setDynamicValues] = useState<Record<string, Record<string, FlowFieldValue>>>({});

  // Accumulate all step data locally — one API call fired at the final step
  const pendingRef = useRef<{
    fields: Record<string, unknown>;
    steps:  Array<{ key: string; completed: boolean; skipped: boolean; data: Record<string, unknown> }>;
  }>({ fields: {}, steps: [] });

  const [isSaving, setIsSaving] = useState(false);
  const [error,    setError]    = useState<string | null>(null);

  const returnTo = useMemo(() => {
    const from = (location.state as { from?: string; resumeStep?: number } | null)?.from;
    if (typeof from === "string" && from.length > 0 && from !== "/buyer/onboarding") return from;
    return "/dashboard";
  }, [location.state]);

  const resolvedFlowIso2 = useMemo(() => {
    if (!countries.length) return null;
    const found = countries.find((c) => c.name === user?.country || c.iso2 === user?.country);
    return (found?.iso2) ?? undefined;
  }, [countries, user?.country]);

  useEffect(() => {
    if (resolvedFlowIso2 === null) return;
    setFlowLoading(true);
    setFlowError(null);
    onboardingFlowPublicService.getFlow("buyer", resolvedFlowIso2)
      .then((flow) => setSteps(flow.steps))
      .catch((err) => setFlowError(parseApiError(err, "Failed to load onboarding steps.").message))
      .finally(() => setFlowLoading(false));
  }, [resolvedFlowIso2]);

  // If launched from the "Complete your profile" nudge, jump to the requested step
  useEffect(() => {
    const resumeStep = (location.state as { resumeStep?: number } | null)?.resumeStep;
    if (resumeStep && steps.length && resumeStep >= 1 && resumeStep <= steps.length) {
      setCurrentStep(resumeStep);
    }
  }, [steps.length, location.state]);

  useEffect(() => {
    if (!isBuyerRole(user?.role)) { navigate("/", { replace: true }); return; }
    if (currentStep === 1 && completedKeys.length === 0 && user?.onboarding?.completed) {
      navigate(returnTo, { replace: true });
    }
  }, [navigate, returnTo, user, currentStep, completedKeys.length]);

  // Fetch the latest profile data from the backend to ensure local storage isn't stale
  useEffect(() => {
    refreshUser().catch(() => {});
  }, [refreshUser]);

  const hasInitializedStep = useRef(false);

  useEffect(() => {
    const userSteps = user?.onboarding?.steps ?? [];
    const done = userSteps.filter((s: any) => s.completed).map((s: any) => s.key);
    if (done.length) setCompletedKeys(done);
    const skipped = userSteps.filter((s: any) => s.skipped).map((s: any) => s.key);
    if (skipped.length) setSkippedKeys(skipped);

    // Resume only once both the flow config and the user profile are loaded.
    if (!hasInitializedStep.current && user && steps.length) {
      const requestedStep = (location.state as { resumeStep?: number } | null)?.resumeStep;
      if (!requestedStep) {
        // Resume at the first step that is neither completed nor skipped, derived
        // from the per-step completion data (the source of truth). The stored
        // current_step counter is unreliable — it can be stale or written
        // backward by the missed-step logic in saveStep — so it's only a fallback.
        const statusByKey = new Map(userSteps.map((s: any) => [s.key, s]));
        let resume = 0;
        for (let i = 0; i < steps.length; i++) {
          const st: any = statusByKey.get(steps[i].key);
          if (!st || (!st.completed && !st.skipped)) { resume = i + 1; break; }
        }
        // Every step done/skipped → land on the last step for review.
        setCurrentStep(resume === 0 ? steps.length : resume);
      }
      hasInitializedStep.current = true;
    }
  }, [user, steps, location.state]);

  // Hydrate dynamicValues from user profile if possible
  useEffect(() => {
    if (!steps.length || !user) return;
    setDynamicValues((prev) => {
      const next = { ...prev };
      steps.forEach((step) => {
        if (!next[step.key]) next[step.key] = {};
        step.fields.forEach((f) => {
          if (next[step.key][f.key] === undefined) {
             const stepData = user.onboarding?.steps?.find((s: any) => s.key === step.key);
             let val = stepData?.data?.[f.key];
             if (val === undefined && f.key === "offices") val = user.offices;
             if (val === undefined) val = (user as any)[f.key];
             if (f.type === "phone") {
               let cc = stepData?.data?.[phoneCountryCodeKey(f.key)];
               // Best-effort backfill: legacy data stored a single combined E.164 string.
               if (cc === undefined && typeof val === "string" && val.startsWith("+")) {
                 const split = splitPhoneE164(val);
                 val = split.number;
                 cc = split.countryCode;
               }
               next[step.key][phoneCountryCodeKey(f.key)] = (cc !== undefined && cc !== null ? cc : "") as FlowFieldValue;
             }
             next[step.key][f.key] = (val !== undefined && val !== null ? val : defaultFieldValue(f)) as FlowFieldValue;
          }
        });
      });
      return next;
    });
  }, [steps, user]);

  const setDynamicField = (stepKey: string, fieldKey: string, value: FlowFieldValue) =>
    setDynamicValues((prev) => ({ ...prev, [stepKey]: { ...prev[stepKey], [fieldKey]: value } }));

  const dynamicStepValues = (step: StepConfig): Record<string, FlowFieldValue> => {
    const stored = dynamicValues[step.key] ?? {};
    const out: Record<string, FlowFieldValue> = {};
    for (const f of step.fields) {
      out[f.key] = stored[f.key] ?? defaultFieldValue(f);
      if (f.type === "phone") out[phoneCountryCodeKey(f.key)] = stored[phoneCountryCodeKey(f.key)] ?? "";
    }
    return out;
  };

  const dynamicStepPayload = (step: StepConfig): Record<string, unknown> => {
    const values = dynamicStepValues(step);
    const payload: Record<string, unknown> = {};
    for (const f of step.fields) {
      const v = values[f.key];
      if (f.type === "phone") {
        if (typeof v === "string" && v.trim()) {
          payload[f.key] = v.trim();
          const cc = values[phoneCountryCodeKey(f.key)];
          if (typeof cc === "string" && cc.trim()) payload[phoneCountryCodeKey(f.key)] = cc.trim();
        }
      } else if (f.type === "number") {
        if (v !== "" && !Number.isNaN(Number(v))) payload[f.key] = Number(v);
      } else if (Array.isArray(v)) {
        if (v.length) payload[f.key] = v;
      } else if (f.type === "checkbox") {
        payload[f.key] = v === true;
      } else if (f.type === "group") {
        if (v && typeof v === "object" && !Array.isArray(v) && Object.keys(v).length) payload[f.key] = v;
      } else if (typeof v === "string") {
        if (v.trim()) payload[f.key] = v.trim();
      }
    }
    return payload;
  };

  const saveStep = async (stepKey: string, payload: Record<string, unknown>, skip = false) => {
    setError(null);

    // Gather all fields from all steps that have been hydrated/edited so far
    const allFieldsPayload: Record<string, unknown> = {};
    steps.forEach((step) => {
      Object.assign(allFieldsPayload, dynamicStepPayload(step));
    });

    pendingRef.current.steps = [
      ...pendingRef.current.steps.filter((s) => s.key !== stepKey),
      { key: stepKey, completed: !skip, skipped: skip, data: payload },
    ];

    if (!skip) {
      setCompletedKeys((prev) => [...new Set([...prev, stepKey])]);
      setSkippedKeys((prev) => prev.filter((k) => k !== stepKey));
    } else {
      setSkippedKeys((prev) => [...new Set([...prev, stepKey])]);
    }

    const completedAfterSave = skip ? completedKeys : [...new Set([...completedKeys, stepKey])];
    const skippedAfterSave = skip ? [...new Set([...skippedKeys, stepKey])] : skippedKeys;
    // Every step — required AND optional — must end up with an explicit
    // completed/skipped record before the wizard exits to /profile, so
    // onboarding.steps[] is always a complete, resolved set (not just the
    // required ones the user happened to touch).
    const allStepsDoneAfterSave = steps.every(
      (s) => completedAfterSave.includes(s.key) || skippedAfterSave.includes(s.key)
    );
    const isLast = currentStep >= steps.length || allStepsDoneAfterSave;

    if (!isLast) {
      // Strictly sequential: advance one step at a time, through optional
      // (skippable) steps as well as required ones. Required steps can't be
      // bypassed anyway — they have no Skip button and Next stays disabled until
      // valid — so we never jump ahead to a later required step (that used to
      // skip over the optional steps sitting in between).
      const nextStep = currentStep + 1;

      try {
        await usersService.updateProfile({
          onboarding_steps: [{ key: stepKey, completed: !skip, skipped: skip, data: payload }],
          current_onboarding_step: nextStep,
        });
        await refreshUser();
      } catch (err) {
        setError(parseApiError(err, "Failed to save. Please try again.").message);
        return;
      }

      setCurrentStep(nextStep);
      return;
    }

    setIsSaving(true);
    try {
      const finalPayload = {
        onboarding_steps: pendingRef.current.steps,
        current_onboarding_step: steps.length,
      };
      await usersService.updateProfile(finalPayload as Parameters<typeof usersService.updateProfile>[0]);
      await refreshUser();
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(parseApiError(err, "Failed to save. Please try again.").message);
    } finally {
      setIsSaving(false);
    }
  };

  const goBack = () => setCurrentStep((s) => Math.max(1, s - 1));

  const skippableStepsRemain = steps.some(
    (s) => !s.required && !completedKeys.includes(s.key) && !skippedKeys.includes(s.key)
  );

  const allRequiredDoneNow = steps.filter((s) => s.required).every((s) => completedKeys.includes(s.key));

  const skipAll = async () => {
    setError(null);
    const skippedNow: string[] = [];
    steps.forEach((s) => {
      if (s.required || completedKeys.includes(s.key) || skippedKeys.includes(s.key)) return;
      skippedNow.push(s.key);
      pendingRef.current.steps = [
        ...pendingRef.current.steps.filter((r) => r.key !== s.key),
        { key: s.key, completed: false, skipped: true, data: {} },
      ];
    });
    if (skippedNow.length) setSkippedKeys((prev) => [...new Set([...prev, ...skippedNow])]);

    const allRequiredDone = steps
      .filter((s) => s.required)
      .every((s) => completedKeys.includes(s.key));

    if (!allRequiredDone) {
      // Land on the first outstanding required step so the user can finish it.
      const nextRequiredIdx = steps.findIndex((s) => s.required && !completedKeys.includes(s.key));
      setIsSaving(true);
      try {
        await usersService.updateProfile({
          onboarding_steps: pendingRef.current.steps,
          current_onboarding_step: nextRequiredIdx >= 0 ? nextRequiredIdx + 1 : currentStep,
        } as Parameters<typeof usersService.updateProfile>[0]);
        await refreshUser();
      } catch (err) {
        setError(parseApiError(err, "Failed to save. Please try again.").message);
        return;
      } finally {
        setIsSaving(false);
      }
      if (nextRequiredIdx >= 0) setCurrentStep(nextRequiredIdx + 1);
      return;
    }

    setIsSaving(true);
    try {
      await usersService.updateProfile({
        onboarding_steps: pendingRef.current.steps,
        current_onboarding_step: steps.length,
      } as Parameters<typeof usersService.updateProfile>[0]);
      await refreshUser();
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(parseApiError(err, "Failed to save. Please try again.").message);
    } finally {
      setIsSaving(false);
    }
  };

  const StepFooter = ({ onNext, nextDisabled = false, onSkip, isRequired, isFinish }: { onNext: () => void; nextDisabled?: boolean; onSkip: () => void; isRequired: boolean; isFinish: boolean }) => (
    <div className="pt-4">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={currentStep === 1 ? () => navigate("/") : goBack}
          className="rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-ink hover:bg-surface-subtle"
        >
          {currentStep === 1 ? "Back to marketplace" : "← Back"}
        </button>
        <div className="flex gap-2">
          {!isRequired && (
            <button
              type="button"
              onClick={onSkip}
              disabled={isSaving}
              className="rounded-lg px-4 py-2.5 text-sm font-medium text-ink-muted hover:text-ink disabled:opacity-40"
            >
              Skip
            </button>
          )}
          <button
            type="button"
            onClick={onNext}
            disabled={nextDisabled || isSaving}
            className="btn-primary px-5 py-2.5 text-sm disabled:opacity-50"
          >
            {isSaving ? "Saving…" : isFinish ? "Finish" : "Save & Continue →"}
          </button>
        </div>
      </div>
    </div>
  );

  if (flowLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-muted">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand/30 border-t-brand" />
          <p className="text-sm font-medium text-ink-muted">Loading your onboarding flow…</p>
        </div>
      </div>
    );
  }

  if (flowError || !steps.length) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-muted p-6">
        <div className="w-full max-w-md rounded-2xl bg-surface p-6 shadow-xl text-center">
          <p className="text-red-500 mb-4">{flowError || "No onboarding steps configured for your region."}</p>
          <button onClick={() => navigate("/")} className="btn-primary px-4 py-2">Return Home</button>
        </div>
      </div>
    );
  }

  const stepDef = steps[currentStep - 1];
  const stepValues = dynamicStepValues(stepDef);

  return (
    <div className="min-h-screen bg-surface-muted pb-20 pt-8 sm:pt-12">
      <div className="mx-auto max-w-2xl px-4 sm:px-6">
        <div className="mb-4">
          <button
            type="button"
            onClick={logout}
            className="text-sm font-medium text-ink-muted hover:text-ink"
          >
            ← Back to sign in
          </button>
        </div>
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">Buyer Onboarding</h1>
          <p className="mt-2 text-sm text-ink-muted">Complete your profile to find travel deals</p>
          {skippableStepsRemain && (
            <button
              type="button"
              onClick={skipAll}
              disabled={isSaving}
              className="mt-3 text-sm font-medium text-ink-muted underline decoration-dotted hover:text-ink disabled:opacity-40"
            >
              Skip all optional steps
            </button>
          )}
        </div>

        <StepperBar
          current={currentStep}
          steps={steps}
          completedKeys={completedKeys}
          skippedKeys={skippedKeys}
          onStepClick={(step) => setCurrentStep(step)}
        />

        <div className="glass-panel p-6">
          {error && <div className="alert-error mb-5">{error}</div>}

          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-ink">{stepDef.title || stepDef.label}</h2>
              {stepDef.description && <p className="mt-1 text-sm text-ink-muted">{stepDef.description}</p>}
            </div>

            {stepDef.fields.map((f) => (
              <FlowFieldRenderer
                key={f.key}
                field={f}
                value={stepValues[f.key]}
                onChange={(val) => setDynamicField(stepDef.key, f.key, val)}
                stepFields={stepDef.fields}
                stepValues={stepValues}
                onChangeKey={(key, val) => setDynamicField(stepDef.key, key, val)}
              />
            ))}

            <StepFooter
              isRequired={stepDef.required}
              isFinish={
                currentStep === steps.length ||
                (allRequiredDoneNow && !(stepDef.required && !completedKeys.includes(stepDef.key)))
              }
              onNext={() => saveStep(stepDef.key, dynamicStepPayload(stepDef))}
              nextDisabled={!isDynamicStepValid(stepDef.fields, stepValues)}
              onSkip={() => saveStep(stepDef.key, {}, true)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyerOnboardingPage;
